import { useTranslation } from 'react-i18next';
import CountryLabel from '../../../components/CountryLabel/CountryLabel';
import { Column, Table } from '../../../components/Table/Table';
import { formatDate, formatNumber } from '../../../core/helpers';
import './SingleItemTab.scss';

interface LocalFilingItem {
  country: string;
  status: string;
  per_report: number;
  reports: number;
  billed: number;
}

const localFilingColumns: Column<LocalFilingItem>[] = [
  {
    label: 'Country',
    field: 'country',
    formatter: (item) => <CountryLabel country={item.country} link={true} />,
    maxWidth: '160px',
  },
  {
    label: 'Status',
    field: 'status',
  },
  {
    label: 'Per report',
    field: 'per_report',
    formatter: (item) => (
      <div>
        {formatNumber(item.per_report / 100, {
          currency: 'eur',
        })}
      </div>
    ),
  },
  {
    label: 'Reports',
    field: 'reports',
    maxWidth: '100px',
  },
  {
    label: 'Billed',
    field: 'billed',
    formatter: (item) => (
      <div>
        {formatNumber(item.billed / 100, {
          currency: 'eur',
        })}
      </div>
    ),
  },
];

interface VATIdItem {
  country: string;
  status: string;
  updated_at: Date;
  billed: number;
}

const VATIdsColumns: Column<VATIdItem>[] = [
  {
    label: 'Country',
    field: 'country',
    formatter: (item) => <CountryLabel country={item.country} link={true} />,
    maxWidth: '160px',
  },
  {
    label: 'Status',
    field: 'status',
  },
  {
    label: 'Last update',
    field: 'updated_at',
    formatter: (item) => <div>{formatDate(item.updated_at, 'dd.MM.yyyy')}</div>,
    maxWidth: '240px',
  },
  {
    label: 'Billed',
    field: 'billed',
    formatter: (item) => (
      <div>
        {formatNumber(item.billed / 100, {
          currency: 'eur',
        })}
      </div>
    ),
  },
];

const LOCAL_FILING_ITEMS = [
  {
    country: 'PL',
    status: 'In progress',
    per_report: 8900,
    reports: 4,
    billed: 45600,
  },
  {
    country: 'CZ',
    status: 'In progress',
    per_report: 8900,
    reports: 8,
    billed: 71200,
  },
  {
    country: 'IT',
    status: 'Prepared',
    per_report: 8900,
    reports: 2,
    billed: 17800,
  },
  {
    country: 'AT',
    status: 'Prepared',
    per_report: 8900,
    reports: 1,
    billed: 8900,
  },
  {
    country: 'ES',
    status: 'Prepared',
    per_report: 8900,
    reports: 4,
    billed: 35600,
  },
];
const VAT_IDS: VATIdItem[] = [
  {
    country: 'PL',
    status: 'Registered',
    updated_at: new Date(2022, 1, 24),
    billed: 44900,
  },
  {
    country: 'CZ',
    status: 'Registered',
    updated_at: new Date(2022, 1, 24),
    billed: 44900,
  },
  {
    country: 'IT',
    status: 'Registered',
    updated_at: new Date(2022, 1, 24),
    billed: 44900,
  },
  {
    country: 'AT',
    status: 'Registered',
    updated_at: new Date(2022, 1, 24),
    billed: 44900,
  },
  {
    country: 'ES',
    status: 'Registered',
    updated_at: new Date(2022, 1, 24),
    billed: 44900,
  },
];
const DEREGISTERED_VAT_IDS: VATIdItem[] = [
  {
    country: 'AT',
    status: 'Deregistered',
    updated_at: new Date(2022, 7, 24),
    billed: 44900,
  },
];

const OTHER_DETAILS = [
  {
    title: 'OSS registration',
    country: 'DE',
    registered_date: new Date(2022, 2, 24),
    total: 13900,
  },
  {
    title: 'Intrastat registration',
    country: 'IT',
    registered_date: new Date(2022, 2, 24),
    total: 48000,
  },
  {
    title: 'Fiscal representative registration',
    country: 'FR',
    registered_date: new Date(2022, 2, 24),
    total: 48000,
  },
];

export default function SingleItemTab() {
  const { t } = useTranslation();
  return (
    <div>
      <div className='Text--20 fw-800'>{t('Retroactive local filing')}</div>
      <div>
        {t(
          "We prepare reports for the periods where you have problems, for instanceyou didn't have active VAT ID. Amounts billed single time."
        )}
      </div>
      <Table<LocalFilingItem>
        columns={localFilingColumns}
        data={LOCAL_FILING_ITEMS}
        className='Table--SingleItemTab px-4 pb-3'
      />
      <div className='SingleItemTab__separator mx-n4' />
      <div className='Text--20 fw-800 pt-4 mb-2'>
        {t('VAT ID registration')}
      </div>
      <Table<VATIdItem>
        columns={VATIdsColumns}
        data={VAT_IDS}
        className='Table--SingleItemTab px-4 pb-3'
      />
      {OTHER_DETAILS.map((d) => (
        <div key={d.title}>
          <div className='SingleItemTab__separator mx-n4' />
          <div className='Text--20 fw-800 pt-3 mb-2'>{t(d.title)}</div>
          <div className='d-grid SingleItemTab__grid pb-4'>
            <div>
              <CountryLabel country={d.country} />
            </div>
            <div>
              {t('Registered on')} {formatDate(d.registered_date, 'dd.MM.yyyy')}
            </div>
            <div>
              {formatNumber(d.total / 100, {
                currency: 'eur',
              })}
            </div>
          </div>
        </div>
      ))}
      <div className='SingleItemTab__separator mx-n4' />
      <div className='Text--20 fw-800 pt-4 mb-2'>
        {t('VAT ID deregistration')}
      </div>
      <Table<VATIdItem>
        columns={VATIdsColumns}
        data={DEREGISTERED_VAT_IDS}
        className='Table--SingleItemTab px-4 pb-3'
      />
    </div>
  );
}
